import {
  Box,
  Button,
  CloudRVRDisabled,
  Link,
} from '@amzn/ring-ui-react-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { config } from 'src/config';
import { useIsDesktop } from 'src/shared/hooks';
import { Device } from '../DeviceSelectorDropdown';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalProps,
} from '../Modal';

interface NoSubscriptionModalProps extends ModalProps {
  onClose: () => void;
  locationId: string | null;
  locationName: string | null;
  selectedDevice?: Device;
}

const { RING_ACCOUNT_URL, RING_URL } = config;

export const NoSubscriptionModal = ({
  isOpen,
  locationId,
  locationName,
  onClose,
  selectedDevice,
  ...props
}: NoSubscriptionModalProps) => {
  const { t } = useTranslation('components.modal');
  const isDesktop = useIsDesktop();
  const url = useMemo(() => {
    let result = `${RING_ACCOUNT_URL}/account/billing/plans`;

    if (locationId) {
      result += `?locationId=${locationId}&unsubscribedDevices=true`;
    }

    return result;
  }, [locationId]);

  const { bodyText, headerText } = useMemo(() => {
    const result = {
      bodyText: t('no-subscription-for-location', { locationName }),
      headerText: t('video-recordings-for-subscribed-users'),
    };

    if (
      selectedDevice &&
      selectedDevice.id &&
      selectedDevice.id !== 'all-cameras'
    ) {
      result.bodyText = t('no-subscription-for-device', {
        deviceName: selectedDevice.name,
      });
      result.headerText = t('video-recordings-for-subscribed-devices');
    }

    return result;
  }, [selectedDevice, locationName, t]);

  return (
    <Modal
      {...props}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size="xlg"
      isCentered
    >
      <ModalOverlay>
        <ModalContent>
          <ModalHeader
            justifyContent="center"
            py={4}
            textStyle="body-normal-medium"
          >
            {t('subscribe-to-a-plan')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={7} px={{ base: 6, m: 8 }} pb={4}>
            <CloudRVRDisabled
              viewBox="0 0 48 48"
              boxSize={12}
              color="primary.500"
              mb={3}
            />
            <Box
              textStyle="heading-normal-medium"
              mb={2}
              data-testid="modal-title"
            >
              {headerText}
            </Box>
            <Box textStyle="body-large-light" data-testid="modal-description">
              {bodyText}
            </Box>
          </ModalBody>
          <ModalFooter
            borderTopWidth={0}
            flexDirection={{ base: 'column-reverse', m: 'row' }}
            pt={4}
            pb={{ base: 4, m: 8 }}
            px={{ base: 6, m: 8 }}
            boxShadow={{ base: 'shadow_bottom500', m: 'none' }}
          >
            <Link
              href={`${RING_URL}/protect-plans`}
              target="_blank"
              width={{ base: '100%', m: 200 }}
              mr={{ base: 0, m: 3 }}
              mt={{ base: 3, m: 0 }}
              _hover={{ textDecoration: 'none' }}
            >
              <Box
                as={Button}
                variant={isDesktop ? 'outline' : 'ghost'}
                colorScheme="primary"
                width="100%"
                data-testid="learn-more"
              >
                {t('learn-more')}
              </Box>
            </Link>
            <Link href={url} target="_blank" width={{ base: '100%', m: 200 }}>
              <Box
                as={Button}
                variant="solid"
                colorScheme="primary"
                width="100%"
                data-testid="subscribe"
              >
                {t('subscribe')}
              </Box>
            </Link>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};
